import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Download, FileSpreadsheet, Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import { downloadProvinceExcelTemplate } from '../../../utils/provinceExcelParser';


function ProvinceFileUploader({
  selectedFile,
  onFileSelect,
  onClearFile,
  disabled,
  provinces,
  userProducts,
}) {
  const [dragError, setDragError] = useState('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    disabled,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    onDrop(acceptedFiles, rejectedFiles) {
      setDragError('');
      if (rejectedFiles.length > 0) {
        setDragError('Solo se aceptan archivos Excel (.xlsx, .xls)');
        return;
      }
      if (acceptedFiles[0]) onFileSelect(acceptedFiles[0]);
    },
  });

  return (
    <section className="rounded-2xl border-2 border-slate-200 bg-white p-6 sm:p-8">
      <div className="mb-6 flex items-start gap-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-gold to-brand-primary">
          <Upload className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="font-montserrat text-xl font-extrabold text-brand-primary sm:text-2xl">
            Paso 2: Sube tu Excel de provincia
          </h2>
          <p className="mt-1 font-montserrat text-sm text-slate-600">
            La fecha reservada se aplicará a todas las filas del archivo.
          </p>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-brand-gold/30 bg-brand-gold/10 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-primary" />
          <div className="min-w-0">
            <p className="mb-2 font-montserrat text-sm font-bold text-brand-primary">Columnas obligatorias</p>
            <div className="grid grid-cols-1 gap-1 font-montserrat text-xs text-slate-700 sm:grid-cols-2">
              {['Nombre y Apellido', 'WhatsApp', 'Provincia', 'Distrito', 'Direccion', 'Direccion de Agencia', 'SKU 1'].map(column => (
                <span key={column} className="flex items-center gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-brand-green" />
                  {column}
                </span>
              ))}
            </div>
            <p className="mt-3 font-montserrat text-xs text-slate-600">
              <strong>Opcionales:</strong> DNI, Cantidad 1-5, SKU 2-5, Cobrar, Adelanto y Notas.
              No se requiere comprobante en el Excel.
            </p>
          </div>
        </div>
      </div>

      {!selectedFile ? (
        <div
          {...getRootProps()}
          className={`mb-4 cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-all sm:p-12 ${
            disabled ? 'cursor-not-allowed opacity-50' : ''
          } ${isDragActive ? 'border-brand-gold bg-brand-gold/5' : 'border-slate-300 hover:border-brand-gold hover:bg-slate-50'}`}
        >
          <input {...getInputProps()} />
          <Upload className="mx-auto mb-3 h-10 w-10 text-brand-gold" />
          <p className="font-montserrat font-semibold text-slate-900">
            {isDragActive ? 'Suelta el archivo aquí' : 'Arrastra el Excel o haz clic para seleccionarlo'}
          </p>
          <p className="mt-1 font-montserrat text-xs text-slate-500">Máximo 200 entregas por lote</p>
        </div>
      ) : (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border-2 border-brand-green/30 bg-brand-green/10 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <FileSpreadsheet className="h-6 w-6 flex-shrink-0 text-brand-green" />
            <div className="min-w-0">
              <p className="truncate font-montserrat text-sm font-semibold text-slate-900">{selectedFile.name}</p>
              <p className="font-montserrat text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
            </div>
          </div>
          <button type="button" onClick={onClearFile} disabled={disabled} className="rounded-lg p-2 hover:bg-red-50 disabled:opacity-50" aria-label="Quitar archivo">
            <X className="h-5 w-5 text-red-600" />
          </button>
        </div>
      )}

      {dragError && <p className="mb-4 rounded-lg bg-red-50 p-3 font-montserrat text-sm text-red-700">{dragError}</p>}

      <button
        type="button"
        disabled={disabled}
        onClick={() => downloadProvinceExcelTemplate(provinces, userProducts)}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 font-montserrat font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
      >
        <Download className="h-5 w-5" />
        Descargar plantilla de provincia
      </button>
    </section>
  );
}

export default ProvinceFileUploader;
